import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ColorHash from 'color-hash';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';

import { fetchTotalStatistics } from 'redux/modules/totalStatistics';

import formatPrice from 'utils/formatPrice';
import formatSinglePlural from 'utils/formatSinglePlural';

const colorHash = new ColorHash();

const styles = theme => ({
  root: {
    position: 'relative',
    boxSizing: 'border-box',
    padding: '8px 16px 0 16px',
  },
  gridItemChart: {
    flexShrink: 1,
  },
  gridTable: {
    marginTop: 16,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  chartContainer: {
    marginTop: 16,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 143,
      height: 143,
      margin: '-76px 0 0 -76px',
      borderRadius: '50%',
      border: `4px solid ${theme.palette.primary.A100}`,
      zIndex: 1,
    },
  },
  chartLegend: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    zIndex: 2,
  },
  listItemIcon: {
    width: 10,
    minWidth: 10,
    height: 10,
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: '50%',
    marginRight: 4,
  },
  button: {
    marginTop: 24,
    minWidth: 180,
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  typographyCaption: {
    marginBottom: -20,
  },
  typographyDateRange: {
    marginTop: 16,
    textAlign: 'center',
  },
});

class EraChartDoughnut extends Component {
  constructor(props) {
    super(props);

    this.state = {
      Doughnut: null,
    };

    this.importDependencies = this.importDependencies.bind(this);
  }

  componentDidMount() {
    this.importDependencies();
    this.props.actions.fetchTotalStatistics({
      triggerLoading: true,
    });
  }

  async importDependencies() {
    const { Doughnut } = await import('react-chartjs-2');

    this.setState({
      Doughnut,
    });
  }

  render() {
    const { filteredEraData, theme, classes, loading, formValues } = this.props;
    const { Doughnut } = this.state;
    const payerKeys = Object.keys(filteredEraData);
    if (payerKeys.length === 0) {
      return null;
    }
    const data = payerKeys.length > 1
      ? payerKeys.map(key => Object.keys(filteredEraData[key]).reduce((accumulator, currentValue) =>
        accumulator
        + (filteredEraData[key][currentValue].Paid ? filteredEraData[key][currentValue].Paid['Paid Reimbs'] : 0)
        + (filteredEraData[key][currentValue]['Partial Regular'] ? filteredEraData[key][currentValue]['Partial Regular']['Paid Reimbs'] : 0), 0))
      : [
        Object.keys(filteredEraData[payerKeys[0]]).reduce((accumulator, currentValue) =>
          accumulator + (filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Paid
            ? filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Paid['Claims Count'] : 0)
          , 0),
        Object.keys(filteredEraData[payerKeys[0]]).reduce((accumulator, currentValue) =>
          accumulator + (filteredEraData[Object.keys(filteredEraData)[0]][currentValue]['Partial Regular']
            ? filteredEraData[Object.keys(filteredEraData)[0]][currentValue]['Partial Regular']['Claims Count'] : 0)
          , 0),
        Object.keys(filteredEraData[payerKeys[0]]).reduce((accumulator, currentValue) =>
          accumulator + (filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Denied
            ? filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Denied['Claims Count'] : 0)
          , 0),
        Object.keys(filteredEraData[payerKeys[0]]).reduce((accumulator, currentValue) =>
          accumulator + (filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Recouped
            ? filteredEraData[Object.keys(filteredEraData)[0]][currentValue].Recouped['Claims Count'] : 0)
          , 0),
      ];
    const labels = payerKeys.length > 1
      ? payerKeys
      : [
        'Paid',
        'Partial',
        'Denied',
        'Recouped',
      ];
    const backgroundColor = payerKeys.length > 1
      ? payerKeys.map(key => colorHash.hex(`00000${key}`.slice(-6)))
      : [
        theme.palette.states.paid,
        theme.palette.states.intake,
        theme.palette.states.rejected,
        theme.palette.states.collection,
      ];
    const totalPaidReimbs = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
      accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
        eraAccumulator
        + (filteredEraData[currentValue][eraCurrentValue].Paid
          ? filteredEraData[currentValue][eraCurrentValue].Paid['Paid Reimbs'] : 0)
        + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
          ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Paid Reimbs'] : 0)
        + (filteredEraData[currentValue][eraCurrentValue].Denied
          ? filteredEraData[currentValue][eraCurrentValue].Denied['Paid Reimbs'] : 0), 0), 0);
    const totalERACount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
      accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
        eraAccumulator
        + 1, 0), 0);
    return (
      <div className={classes.root}>
        <Grid
          container
          spacing={0}
          alignItems="center"
          justify="space-between"
        >
          <Grid item xs={12}>
            <Typography>
              {payerKeys.length > 1 ? 'Payer Mix' : 'Payment status'}
            </Typography>
            <Typography variant="caption" className={classes.typographyCaption}>
              {payerKeys.length > 1 ? 'by total paid' : payerKeys[0]}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            {typeof Doughnut !== 'function' || loading > 0 ? (
              <Progress />
            ) : (
              <div className={classes.chartContainer}>
                <div className={classes.chartLegend}>
                  <Typography
                    variant="headline"
                    align="center"
                  >
                    {totalERACount}
                  </Typography>
                  <Typography
                    align="center"
                    variant="caption"
                  >
                    {formatSinglePlural({ quantity: totalERACount, single: 'ERA', plural: 'ERAs' })}
                  </Typography>
                </div>
                <Doughnut
                  width={150}
                  height={150}
                  data={{
                    labels,
                    datasets: [{
                      data,
                      backgroundColor,
                      borderWidth: payerKeys.length > 1 ? payerKeys.map(() => 2) : [2, 2, 2, 2],
                      borderColor: payerKeys.length > 1 ? payerKeys.map(() => theme.palette.common.white)
                        : [theme.palette.common.white, theme.palette.common.white, theme.palette.common.white],
                      hoverBorderColor: payerKeys.length > 1 ? payerKeys.map(() => theme.palette.common.white)
                        : [theme.palette.common.white, theme.palette.common.white, theme.palette.common.white],
                      hoverBackgroundColor: backgroundColor,
                    }],
                  }}
                  options={{
                    responsive: false,
                    width: 150,
                    height: 150,
                    cutoutPercentage: 83,
                    events: [],
                  }}
                  legend={{
                    display: false,
                  }}
                />
              </div>
            )}
            <Typography variant="caption" className={classes.typographyDateRange} gutterBottom>
              {formValues['Date of Creation From']} - {formValues['Date of Creation To']}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <List>
              {payerKeys.length > 1 ? payerKeys.map(key => {
                const paidReimbs = Object.keys(filteredEraData[key]).reduce((accumulator, currentValue) =>
                  accumulator
                  + (filteredEraData[key][currentValue].Paid ? filteredEraData[key][currentValue].Paid['Paid Reimbs'] : 0)
                  + (filteredEraData[key][currentValue]['Partial Regular'] ? filteredEraData[key][currentValue]['Partial Regular']['Paid Reimbs'] : 0)
                  , 0);
                return (
                <ListItem
                  key={key}
                  dense
                  disableGutters
                >
                  <div
                    className={classes.listItemIcon}
                    style={{ borderColor: colorHash.hex(`00000${key}`.slice(-6)) }}
                  />
                  <Typography variant="caption">
                    {key} {((paidReimbs / totalPaidReimbs) * 100).toFixed(2)  }%
                  </Typography>
                </ListItem>
              )}) : (
                <Fragment>
                  <ListItem
                    key="Paid"
                    dense
                    disableGutters
                  >
                    <div
                      className={classes.listItemIcon}
                      style={{ borderColor: theme.palette.states.paid }}
                    />
                    <Typography variant="caption">
                      Paid {data[0]} ({((data[0] / (data[0] + data[1] + data[2] + data[3])) * 100).toFixed(2)}%)
                    </Typography>
                  </ListItem>
                  <ListItem
                    key="Partial"
                    dense
                    disableGutters
                  >
                    <div
                      className={classes.listItemIcon}
                      style={{ borderColor: theme.palette.states.intake }}
                    />
                    <Typography variant="caption">
                      Partial {data[1]} ({((data[1] / (data[0] + data[1] + data[2] + data[3])) * 100).toFixed(2)}%)
                    </Typography>
                  </ListItem>
                  <ListItem
                    key="Denied"
                    dense
                    disableGutters
                  >
                    <div
                      className={classes.listItemIcon}
                      style={{ borderColor: theme.palette.states.rejected }}
                    />
                    <Typography variant="caption">
                      Denied {data[2]} ({((data[2] / (data[0] + data[1] + data[2] + data[3])) * 100).toFixed(2)}%)
                    </Typography>
                  </ListItem>
                  <ListItem
                    key="Recouped"
                    dense
                    disableGutters
                  >
                    <div
                      className={classes.listItemIcon}
                      style={{ borderColor: theme.palette.states.collection }}
                    />
                    <Typography variant="caption">
                      Recouped {data[3]} ({((data[3] / (data[0] + data[1] + data[2] + data[3])) * 100).toFixed(2)}%)
                    </Typography>
                  </ListItem>
                </Fragment>
              )}
            </List>
          </Grid>
        </Grid>
      </div>
    );
  }
}

EraChartDoughnut.propTypes = {
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        500: PropTypes.string.isRequired,
        A100: PropTypes.string.isRequired,
        A400: PropTypes.string.isRequired,
      }).isRequired,
      grey: PropTypes.shape({
        600: PropTypes.string.isRequired,
      }).isRequired,
      common: PropTypes.shape({
        white: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  totalStatistics: PropTypes.shape({
    'Products in storage': PropTypes.number,
    Intake: PropTypes.number,
    'Back order products': PropTypes.number,
    Collection: PropTypes.number,
    Delivery: PropTypes.number,
    Billing: PropTypes.number,
    Paid: PropTypes.number,
  }).isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchTotalStatistics: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridChart: PropTypes.string.isRequired,
    gridItemChart: PropTypes.string.isRequired,
    gridTable: PropTypes.string.isRequired,
    chartContainer: PropTypes.string.isRequired,
    chartLegend: PropTypes.string.isRequired,
    listItemIcon: PropTypes.string.isRequired,
    listItemIconIntake: PropTypes.string.isRequired,
    listItemIconDelivery: PropTypes.string.isRequired,
    listItemIconBilling: PropTypes.string.isRequired,
    listItemIconCollection: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = store => ({
  totalStatistics: store.totalStatistics.data,
  loading: store.totalStatistics.loading,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchTotalStatistics }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles, { withTheme: true })(EraChartDoughnut),
);



// WEBPACK FOOTER //
// ./src/components/home/EraChart/EraChartDoughnut.js